'use client';
import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
} from 'react';
// Type-only import: a runtime import here would pull the player back into the main bundle
import type { DotLottieReact as LottiePlayer } from '@lottiefiles/dotlottie-react';

// Matches $breakpointsDown md in config/_variable.scss
const isMobileViewport = () => window.innerWidth <= 767;

// The player lives in its own chunk, fetched only through this import()
const loadPlayer = () =>
  import('@lottiefiles/dotlottie-react').then((m) => ({
    default: m.DotLottieReact,
  }));

const LazyLottiePlayer = lazy(loadPlayer);

// Desktop: start downloading the chunk as soon as this module runs, without waiting for render
if (typeof window !== 'undefined' && !isMobileViewport()) {
  void loadPlayer();
}

type DotLottieProps = ComponentProps<typeof LottiePlayer>;

// Wrapper around the Lottie player:
// - Desktop: renders the player right after mount (chunk is already downloading).
// - Mobile: waits until the animation is near the viewport before loading the player.
export default function DotLottieReact(props: DotLottieProps) {
  const placeholderRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const element = placeholderRef.current;
    if (!isMobileViewport() || typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return;
    }
    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      // Start loading a little before it scrolls into view
      { rootMargin: '200px' }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Empty box of the same size so the layout doesn't jump
  if (!shouldLoad) {
    return (
      <div
        ref={placeholderRef}
        className={props.className}
        style={props.style}
      />
    );
  }

  return (
    <Suspense
      fallback={<div className={props.className} style={props.style} />}
    >
      <LazyLottiePlayer {...props} />
    </Suspense>
  );
}
